import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

const APP_STORE_URL = 'https://apps.apple.com/app/lambada/id000000000'; // TODO: replace with real App Store ID
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.lambadapro.app';

function getStoreUrl(): string {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return APP_STORE_URL;
  if (/Android/.test(ua)) return PLAY_STORE_URL;
  // Desktop — send to landing page
  return '/';
}

export default function UserProfileRedirectPage() {
  const { userId } = useParams<{ userId: string }>();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!userId) return;

    const deepLink = `lambada://user/${encodeURIComponent(userId)}`;

    // Try to open the app via custom scheme.
    // If the app is installed, it intercepts this and the page stays hidden.
    // If not installed, the browser stays visible and the timer fires → store redirect.
    window.location.href = deepLink;

    timerRef.current = setTimeout(() => {
      window.location.replace(getStoreUrl());
    }, 1800);

    const onVisibilityChange = () => {
      if (document.hidden && timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [userId]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 text-center">
      <div className="max-w-sm w-full space-y-6">
        {/* Logo */}
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-2xl bg-indigo-600 flex items-center justify-center shadow-lg">
            <span className="text-white text-3xl font-bold">L</span>
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900">Opening Lambada&hellip;</h1>
          <p className="text-gray-500 text-sm leading-relaxed">
            If the app doesn&rsquo;t open automatically, download it below.
          </p>
        </div>

        {/* Store buttons */}
        <div className="flex flex-col gap-3">
          <a
            href={APP_STORE_URL}
            className="flex items-center justify-center gap-3 bg-black text-white rounded-xl px-6 py-3.5 font-medium text-sm hover:bg-gray-900 transition-colors"
          >
            <AppleIcon />
            <span>Download on the App Store</span>
          </a>
          <a
            href={PLAY_STORE_URL}
            className="flex items-center justify-center gap-3 bg-black text-white rounded-xl px-6 py-3.5 font-medium text-sm hover:bg-gray-900 transition-colors"
          >
            <PlayIcon />
            <span>Get it on Google Play</span>
          </a>
        </div>

        <p className="text-xs text-gray-400">
          Already installed?{' '}
          <button
            className="text-indigo-600 underline"
            onClick={() => {
              if (userId) window.location.href = `lambada://user/${encodeURIComponent(userId)}`;
            }}
          >
            Open profile
          </button>
        </p>
      </div>
    </div>
  );
}

function AppleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 20.5v-17c0-.83.94-1.3 1.6-.8l14 8.5c.6.37.6 1.23 0 1.6l-14 8.5c-.66.5-1.6.03-1.6-.8z"/>
    </svg>
  );
}
